import { useMemo, useState } from "react";
import { Badge } from "react-bootstrap";
import { AdminListTable } from "@components/CatalogIndex";
import SlideOutMenu from "@components/SlideOutMenuV2/SlideOutMenu";
import { useWebhookPublicationsList } from "../api/useWebhookPublicationsList";
import { PublicationDetail, statusVariant } from "./PublicationDetail";
import type { WebhookPublication } from "../types";

const PAGE_SIZE = 20;

export interface PublicationsTabProps {
  webhookSubscriptionId: string;
}

export const PublicationsTab = ({
  webhookSubscriptionId,
}: PublicationsTabProps): JSX.Element => {
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [selected, setSelected] = useState<WebhookPublication | null>(null);

  const { rows, pageMeta, isFetching } = useWebhookPublicationsList({
    webhookSubscriptionId,
    page: { number: pageNumber, size: pageSize },
  });

  const columns = useMemo(
    () => [
      {
        header: "Status",
        id: "status",
        cell: ({ row }: any) => (
          <Badge
            bg={statusVariant(row.original.status)}
            className="text-white text-capitalize"
          >
            {row.original.status}
          </Badge>
        ),
      },
      {
        header: "HTTP Status",
        id: "httpStatus",
        accessorFn: (r: WebhookPublication) =>
          r.httpStatus != null ? String(r.httpStatus) : "—",
      },
      {
        header: "Source",
        id: "source",
        accessorFn: (r: WebhookPublication) =>
          r.sourceType ? `${r.sourceType} #${r.sourceId ?? "—"}` : "—",
      },
      {
        header: "Created At",
        id: "createdAt",
        accessorFn: (r: WebhookPublication) =>
          r.createdAt ? new Date(r.createdAt).toLocaleString() : "—",
      },
      {
        header: "",
        id: "view",
        cell: ({ row }: any) => (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setSelected(row.original)}
            aria-label={`View publication #${row.original.id}`}
          >
            View
          </button>
        ),
      },
    ],
    []
  );

  return (
    <>
      <div className="admin-list-card--bare">
        <AdminListTable<WebhookPublication>
          columns={columns}
          data={rows}
          isLoading={isFetching}
          selectable={false}
          selectedIds={[]}
          setSelectedIds={() => {}}
          emptyMessage="No publications yet."
          paginationEnabled
          pageSizeEnabled
          totalPages={pageMeta.pageCount}
          totalRows={pageMeta.recordCount}
          currentPage={pageNumber}
          setCurrentPage={setPageNumber}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPageNumber(1);
          }}
        />
      </div>

      <SlideOutMenu
        isOpen={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? `Publication #${selected.id}` : ""}
      >
        {selected && <PublicationDetail publication={selected} />}
      </SlideOutMenu>
    </>
  );
};
